import { Injectable } from "@angular/core";
import {
    HttpRequest,
    HttpHandler,
    HttpEvent,
    HttpInterceptor,
} from "@angular/common/http";
import { Observable } from "rxjs/Observable";
import { environment } from "../../../environments/environment";
import { NbAuthService, NbAuthToken } from "@nebular/auth";
import { switchMap, catchError } from "rxjs/operators";
import { HelperService } from "../helper/helper.service";
import { throwError } from "rxjs";
import { Router } from "@angular/router";
import { isInvalidMfaTokenMessage, MFA_ERROR_CODES } from "app/models/auth/mfa.constants";
const CUSTOMER_URI = '/v1/customers';
const USERS_URI = '/v2/users';

@Injectable()
export class ApiKeyHttpInterceptor implements HttpInterceptor {
    token: string;
    tokenValid: boolean = true;
    unAuthorizedError: number = 401;
    constructor(
        private auth: NbAuthService,
        private helperService: HelperService,
        private nbAuthService: NbAuthService,
        private router: Router
    ) { }

    intercept(
        request: HttpRequest<any>,
        next: HttpHandler
    ): Observable<HttpEvent<any>> {
        // Login MFA setup carries mfa-token — do not rewrite with Bearer + apikey.
        if (request.headers.has('mfa-token')) {
            request = request.clone({
                setParams: {
                    apikey: environment.AUTH.API_KEY,
                },
            });

            return next.handle(request);
        }
        let authHeader =
            "Basic " +
            btoa(environment.AUTH.APP_ID + ":" + environment.AUTH.APP_PWD);
        const excludeUrls = [
            environment.AUTH.AUTH_BASE + environment.AUTH.AUTH_URL,
            environment.BASE_URL + CUSTOMER_URI + '/invite/',
            environment.BASE_URL + USERS_URI + '/invite/'
        ];
        if (!excludeUrls.some((url) => request.url.includes(url))) {
            return this.auth.getToken().pipe(
                switchMap((token: NbAuthToken) => {
                    authHeader = "Bearer " + token.getValue();
                    request = request.clone({
                        setHeaders: {
                            Authorization: authHeader,
                        },
                        setParams: {
                            apikey: environment.AUTH.API_KEY,
                        },
                    });
                    return next.handle(request).pipe(
                        catchError((error) => {
                            const status = error?.status;
                            const onLogin = (this.router.url || '').includes('auth/login');
                            const isAuthTokenCall = request.url.includes(environment.AUTH.AUTH_URL);

                            if (
                                status === this.unAuthorizedError &&
                                !isAuthTokenCall &&
                                !onLogin
                            ) {
                                // MFA soft errors (setup required / OTP / invalid token): no session expiry.
                                // MFA_SETUP_REQUIRED UI is handled by StepUpMfaService (instruction dialog).
                                if (!this.isMfaSoftError(error)) {
                                    this.nbAuthService.isAuthenticatedOrRefresh().subscribe(
                                        (isValid) => {
                                            if (!isValid) {
                                                this.helperService.showAlertBox();
                                            }
                                        },
                                        () => {
                                            this.helperService.showAlertBox();
                                        }
                                    );
                                }
                            }
                            return throwError(error);
                        })
                    );
                })
            );
        } else {
            request = request.clone({
                setHeaders: {
                    Authorization: authHeader,
                },
                setParams: {
                    apikey: environment.AUTH.API_KEY,
                },
            });
            return next.handle(request);
        }
    }

    private isMfaSoftError(error: any): boolean {
        return (
            this.errorContainsCode(error, MFA_ERROR_CODES.SETUP_REQUIRED) ||
            this.errorContainsCode(error, MFA_ERROR_CODES.OTP_REQUIRED) ||
            isInvalidMfaTokenMessage(this.extractErrorMessage(error))
        );
    }

    private extractErrorMessage(error: any): string | undefined {
        const body = error?.error;
        if (typeof body === 'string') {
            try {
                const parsed = JSON.parse(body);
                return parsed?.message || body;
            } catch {
                return body;
            }
        }
        return body?.message || body?.error?.message || body?.errors?.[0]?.message || error?.message;
    }

    private errorContainsCode(error: any, code: string): boolean {
        const extracted = this.extractErrorCode(error?.error) || this.extractErrorCode(error);
        if (extracted === code) {
            return true;
        }
        try {
            const raw =
                typeof error?.error === 'string'
                    ? error.error
                    : JSON.stringify(error?.error ?? {});
            return typeof raw === 'string' && new RegExp(`"${code}"|'${code}'|\\b${code}\\b`).test(raw);
        } catch {
            return false;
        }
    }

    private extractErrorCode(body: any): string | undefined {
        if (!body) {
            return undefined;
        }
        if (typeof body === 'string') {
            try {
                return this.extractErrorCode(JSON.parse(body));
            } catch {
                return body.indexOf(MFA_ERROR_CODES.SETUP_REQUIRED) !== -1
                    ? MFA_ERROR_CODES.SETUP_REQUIRED
                    : body.indexOf(MFA_ERROR_CODES.OTP_REQUIRED) !== -1
                        ? MFA_ERROR_CODES.OTP_REQUIRED
                        : undefined;
            }
        }
        return (
            body.error_code ||
            body.errorCode ||
            body.code ||
            body.error?.error_code ||
            body.error?.errorCode ||
            body.errors?.[0]?.error_code ||
            body.errors?.[0]?.code ||
            body.errors?.[0]?.errorCode
        );
    }
}
